/**
 * appWindowEventGate
 *
 * Single law for app-level window/document command listeners (keydown, keyup,
 * paste, copy, cut). Keep-alive leaves N app trees mounted; a system modal
 * does not change `spaceStore.activeWindowId`. Every such listener must go
 * through `shouldHandleAppEvent` (via `useAppWindowListener`) so inactive
 * apps — and the "active" app behind an overlay — cannot steal paste/undo.
 *
 * Global shortcuts (`globalShortcutsService`) and Space chrome stay outside
 * this gate. Gesture listeners (mousemove/mouseup) stay registered even when
 * inactive — they finish an in-progress drag.
 *
 * Debug: `?debug_app_events=1` or localStorage `debug_app_events=1`
 *   → verbose ignore logs + `window.__appEventGate.dump()`.
 */

import { htmlElementFromTarget, isTypingInInput } from '../utils/keyboardUtils';
import useSpaceStore from '../stores/spaceStore';

export const APP_WINDOW_ID_ATTR = 'data-window-id';
export const APP_EVENT_OWNER_ATTR = 'data-app-event-owner';

export interface AppWindowLookup {
  activeWindowId: string | null;
  windows: Record<string, {
    isTab?: boolean;
    parentWindowId?: string;
    activeTabId?: string;
  }>;
}

export interface ShouldHandleAppEventOptions {
  allowWhenTyping?: boolean;
  store?: AppWindowLookup;
}

type IgnoreReason = 'suspended' | 'inactive-window' | 'foreign-input';

interface IgnoreRecord {
  at: number;
  windowId: string | undefined;
  type: string;
  reason: IgnoreReason;
}

const MAX_IGNORE_LOG = 40;

const suspendStack: string[] = [];
const ignoreLog: IgnoreRecord[] = [];

function isDebugEnabled(): boolean {
  if (typeof window === 'undefined') return false;
  try {
    return (
      new URLSearchParams(window.location.search).get('debug_app_events') === '1' ||
      localStorage.getItem('debug_app_events') === '1'
    );
  } catch {
    return false;
  }
}

function defaultStore(): AppWindowLookup {
  return useSpaceStore.getState();
}

function recordIgnore(windowId: string | undefined, event: Event, reason: IgnoreReason): void {
  ignoreLog.push({ at: Date.now(), windowId, type: event.type, reason });
  if (ignoreLog.length > MAX_IGNORE_LOG) ignoreLog.shift();
  if (isDebugEnabled()) {
    console.debug('[appEventGate] ignore', { windowId, type: event.type, reason, suspend: [...suspendStack] });
  }
}

/**
 * Element used for "is the user typing?" / eventBelongsToWindow checks.
 * Native keydown/paste target the focused node. `window.dispatchEvent` and
 * some synthetic events leave `target` as Window/null — fall back to
 * `document.activeElement` so a focused invite/chat field is still detected.
 */
function resolveEditableTarget(event: Event): HTMLElement | null {
  const fromTarget = htmlElementFromTarget(event.target);
  if (fromTarget) return fromTarget;
  if (typeof event.composedPath === 'function') {
    for (const node of event.composedPath()) {
      const el = htmlElementFromTarget(node);
      if (el) return el;
    }
  }
  if (typeof document !== 'undefined') {
    return htmlElementFromTarget(document.activeElement);
  }
  return null;
}

/**
 * Whether `windowId` is the focused Space window, including WindowGroup tabs.
 *
 * `activeWindowId` tracks the group *shell*, not the visible tab — a bare
 * equality check is permanently false for grouped tabs. Count a tab as active
 * when its parent shell is focused AND it is that shell's `activeTabId`.
 *
 * No windowId (standalone / tests) → true.
 */
export function isActiveAppWindow(
  windowId: string | undefined,
  store?: AppWindowLookup,
): boolean {
  if (!windowId) return true;
  const s = store ?? defaultStore();
  if (windowId === s.activeWindowId) return true;
  const win = s.windows[windowId];
  if (win?.isTab && win.parentWindowId && win.parentWindowId === s.activeWindowId) {
    return s.windows[win.parentWindowId]?.activeTabId === windowId;
  }
  return false;
}

export function isAppEventsSuspended(): boolean {
  return suspendStack.length > 0;
}

/**
 * Push a named overlay onto the suspend stack. App command listeners no-op
 * until every disposer has run. Calling the disposer twice is a no-op.
 */
export function suspendAppEvents(reason: string): () => void {
  suspendStack.push(reason);
  if (isDebugEnabled()) {
    console.debug('[appEventGate] suspend', { reason, depth: suspendStack.length });
  }
  let released = false;
  return () => {
    if (released) return;
    released = true;
    const idx = suspendStack.lastIndexOf(reason);
    if (idx >= 0) suspendStack.splice(idx, 1);
    if (isDebugEnabled()) {
      console.debug('[appEventGate] resume', { reason, depth: suspendStack.length });
    }
  };
}

function escapeAttrValue(value: string): string {
  if (typeof CSS !== 'undefined' && typeof CSS.escape === 'function') {
    return CSS.escape(value);
  }
  return value.replace(/\\/g, '\\\\').replace(/"/g, '\\"');
}

/**
 * True when `target` lives inside this window's chrome (`data-window-id`) or
 * an app-owned portal stamped with `data-app-event-owner`.
 */
export function eventBelongsToWindow(
  target: EventTarget | null,
  windowId: string,
): boolean {
  if (!windowId) return false;
  let el: Element | null = null;
  if (target instanceof Element) {
    el = target;
  } else if (target instanceof Node) {
    el = target.parentElement;
  }
  if (!el) return false;
  const escaped = escapeAttrValue(windowId);
  return !!(
    el.closest(`[${APP_WINDOW_ID_ATTR}="${escaped}"]`) ||
    el.closest(`[${APP_EVENT_OWNER_ATTR}="${escaped}"]`)
  );
}

/**
 * Default gate for app-window command listeners.
 *
 * 1. System overlay suspend → false
 * 2. This window is not the active Space window → false
 * 3. Event target is an editable field that does not belong to this window → false
 */
export function shouldHandleAppEvent(
  windowId: string | undefined,
  event: Event,
  opts?: ShouldHandleAppEventOptions,
): boolean {
  if (isAppEventsSuspended()) {
    recordIgnore(windowId, event, 'suspended');
    return false;
  }
  if (!isActiveAppWindow(windowId, opts?.store)) {
    recordIgnore(windowId, event, 'inactive-window');
    return false;
  }
  const typingTarget = resolveEditableTarget(event);
  if (
    windowId &&
    !opts?.allowWhenTyping &&
    isTypingInInput(typingTarget) &&
    !eventBelongsToWindow(typingTarget, windowId)
  ) {
    recordIgnore(windowId, event, 'foreign-input');
    return false;
  }
  return true;
}

export function getAppEventGateDump(): {
  suspended: boolean;
  suspendStack: string[];
  activeWindowId: string | null;
  recentIgnores: IgnoreRecord[];
} {
  return {
    suspended: isAppEventsSuspended(),
    suspendStack: [...suspendStack],
    activeWindowId: defaultStore().activeWindowId,
    recentIgnores: [...ignoreLog],
  };
}

/** Test-only: clear suspend stack and ignore log. */
export function resetAppWindowEventGateForTests(): void {
  suspendStack.length = 0;
  ignoreLog.length = 0;
}

declare global {
  interface Window {
    __appEventGate?: {
      dump: typeof getAppEventGateDump;
      isSuspended: typeof isAppEventsSuspended;
      isActiveAppWindow: typeof isActiveAppWindow;
    };
  }
}

if (typeof window !== 'undefined') {
  window.__appEventGate = {
    dump: getAppEventGateDump,
    isSuspended: isAppEventsSuspended,
    isActiveAppWindow,
  };
}
